// Дискорд-стайл селектор микрофона: список устройств, «Как в системе»,
// живой voice meter ТОЛЬКО у активного устройства (параллельный захват всех
// микрофонов увёл бы Bluetooth в HFP прямо от открытия меню), у остальных —
// статичная иконка полосок.

import { useEffect, useRef, useState } from 'preact/hooks';
import type { LocalAudioTrack } from 'livekit-client';
import * as micDevice from '../lib/micDevice.js';

interface MicPickerProps {
  micTrack: LocalAudioTrack | null;
  /** Идёт raw-запись: уровень берём из рекордера, а не из AnalyserNode. */
  recActive: boolean;
  recLevel: number;
  onSelect: (choice: 'default' | { deviceId: string; label: string }) => void;
}

/** Мини-эквалайзер: 5 столбиков, заполнение по уровню (RMS 0..1). */
function MeterBars({ level, active }: { level: number; active: boolean }) {
  const lit = active ? Math.min(5, Math.ceil(Math.min(1, level * 3) * 5)) : 0;
  return (
    <span class={`micBars${active ? ' live' : ''}`} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} class={`micBar${i < lit ? ' lit' : ''}`} style={{ height: `${5 + i * 2}px` }} />
      ))}
    </span>
  );
}

export function MicPicker({ micTrack, recActive, recLevel, onSelect }: MicPickerProps) {
  const [open, setOpen] = useState(false);
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [analyserLevel, setAnalyserLevel] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const currentLabel = micTrack?.mediaStreamTrack.label || '';
  const currentDeviceId = micTrack?.mediaStreamTrack.getSettings().deviceId || '';
  const activeChoice = micDevice.getActiveChoice();

  const refresh = () => {
    void micDevice.listInputs().then(setInputs).catch(() => setInputs([]));
  };

  // Список: на открытие + на devicechange/смену выбора
  useEffect(() => {
    if (!open) return;
    refresh();
    return micDevice.onChange(refresh);
  }, [open]);

  // Закрытие: клик мимо и Escape
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Живой метр вне записи: AnalyserNode на клоне LiveKit-трека, живёт только
  // пока меню открыто. Во время записи уровень уже считает рекордер.
  useEffect(() => {
    if (!open || recActive || !micTrack) return;
    const source = micTrack.mediaStreamTrack;
    if (source.readyState !== 'live') return;
    const clone = source.clone();
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(new MediaStream([clone])).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const timer = window.setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      setAnalyserLevel(Math.sqrt(sum / buf.length));
    }, 120);
    return () => {
      window.clearInterval(timer);
      clone.stop();
      void ctx.close().catch(() => {});
      setAnalyserLevel(0);
    };
  }, [open, recActive, micTrack]);

  const level = recActive ? recLevel : analyserLevel;

  // Псевдо-устройства default/communications не показываем отдельными строками —
  // их представляет «Как в системе» (label вида "Default — X" даёт резолв имени).
  const defaultInfo = inputs.find((d) => d.deviceId === 'default');
  const realInputs = inputs.filter(
    (d) => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications'
  );
  const systemResolved = defaultInfo?.label.replace(/^default\s*[-—–]\s*/i, '') || '';

  const isActive = (deviceId: string) =>
    activeChoice === deviceId || (activeChoice === 'default' && deviceId === currentDeviceId);

  return (
    <div class="micPicker" ref={rootRef}>
      <button
        class="micPickerBtn"
        title="Выбрать микрофон. Смена во время записи даёт 1-2 секунды тишины в дорожке на стыке."
        onClick={() => setOpen(!open)}
      >
        🎙 <span class="micPickerLabel">{currentLabel || 'Микрофон'}</span> ▾
      </button>
      {open && (
        <div class="micMenu">
          <div class="micMenuHeader">Микрофоны</div>
          <button
            class={`micRow${activeChoice === 'default' ? ' active' : ''}`}
            onClick={() => {
              setOpen(false);
              onSelect('default');
            }}
          >
            <MeterBars level={level} active={activeChoice === 'default'} />
            <span class="micRowText">
              Как в системе
              {systemResolved && <span class="micRowSub">{systemResolved}</span>}
            </span>
            {activeChoice === 'default' && <span class="micCheck">✓</span>}
          </button>
          {realInputs.map((device) => (
            <button
              key={device.deviceId}
              class={`micRow${isActive(device.deviceId) && activeChoice !== 'default' ? ' active' : ''}`}
              onClick={() => {
                setOpen(false);
                onSelect({ deviceId: device.deviceId, label: device.label });
              }}
            >
              <MeterBars level={level} active={isActive(device.deviceId)} />
              <span class="micRowText">{device.label || 'Микрофон без названия'}</span>
              {isActive(device.deviceId) && activeChoice !== 'default' && <span class="micCheck">✓</span>}
            </button>
          ))}
          {realInputs.length === 0 && !defaultInfo && (
            <div class="micRowSub" style={{ padding: '8px 10px' }}>
              Устройства не видны — дайте доступ к микрофону
            </div>
          )}
        </div>
      )}
    </div>
  );
}
